import React, { useEffect, useRef } from "react";
import { LuX } from "react-icons/lu";
import type { Shot } from "./ProductShot";

// Native <dialog>, same as the enquiry form: showModal() brings the focus trap, the inert
// background and Esc with no key handling of our own. The card shrink-wraps the image so a tall
// portrait shot is not stranded in a wide white panel.
export default function ProductLightbox({
  shot,
  onClose,
}: {
  shot: Shot;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    dialog.showModal();

    // showModal() leaves the page behind it scrollable, which drags the dialog off screen on iOS.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      // The card shrink-wraps, so the closable backdrop is anything outside it rather than the
      // dialog element alone.
      onClick={(event) => {
        if (!(event.target as HTMLElement).closest("[data-shot-card]")) dialogRef.current?.close();
      }}
      aria-label={shot.title}
      className="tw-m-auto tw-w-[min(1180px,94vw)] tw-max-w-none tw-border-0 tw-bg-transparent tw-p-0"
    >
      <div className="tw-flex tw-justify-center">
        <div
          data-shot-card
          className="tw-relative tw-rounded-[24px] tw-bg-white tw-p-3 tw-shadow-[0_30px_90px_rgba(9,32,58,.45)] sm:tw-p-2"
        >
          <button
            type="button"
            onClick={() => dialogRef.current?.close()}
            aria-label="Close"
            className="tw-absolute tw-right-4 tw-top-4 tw-z-10 tw-inline-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-[#c9deed] tw-bg-white tw-text-[#304d68] tw-transition-colors hover:tw-bg-[#eaf4fc] focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-[#087bd3]"
          >
            <LuX aria-hidden className="tw-h-5 tw-w-5" />
          </button>

          <img
            src={shot.src}
            alt={shot.alt}
            width={shot.width}
            height={shot.height}
            className="tw-block tw-h-auto tw-w-auto tw-max-h-[86vh] tw-max-w-[min(1156px,90vw)] tw-rounded-[16px]"
          />
        </div>
      </div>
    </dialog>
  );
}
